import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { useAction, useQuery } from "convex/react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Plus,
  UserPlus,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { StatutCampagneEnum } from "../../../../convex/schema";
import { AjouterCandidatsDialog } from "./AjouterCandidatsDialog";

type FiltreStatutCampagne = "tous" | "brouillon" | "active" | "terminee";

const PAGE_SIZE = 10;

export function CampagneList() {
  const [page, setPage] = useState(1);
  const [filtreStatut, setFiltreStatut] = useState<FiltreStatutCampagne>(
    "tous",
  );
  const [sendingEmails, setSendingEmails] = useState(false);
  const [addCandidatsCampagne, setAddCandidatsCampagne] = useState<{
    id: Id<"campagnes">;
    nom: string;
    anonyme?: boolean;
  } | null>(null);
  const [emailModalCampagneId, setEmailModalCampagneId] =
    useState<Id<"campagnes"> | null>(null);
  const [selectedParcoursIds, setSelectedParcoursIds] = useState<
    Id<"parcoursEvaluations">[]
  >([]);

  const envoyerLiensParcoursSelectionnes = useAction(
    api.email.envoyerLiensParcoursSelectionnes,
  );

  const result = useQuery(api.campagnes.listerCampagnes, {
    page,
    pageSize: PAGE_SIZE,
    filtreStatut,
  });

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case StatutCampagneEnum.ACTIVE:
        return <Badge variant="success">Active</Badge>;
      case StatutCampagneEnum.TERMINEE:
        return <Badge variant="secondary">Terminée</Badge>;
      case StatutCampagneEnum.BROUILLON:
        return <Badge variant="outline">Brouillon</Badge>;
      default:
        return <Badge variant="outline">{statut}</Badge>;
    }
  };

  const campagneStats = useQuery(
    api.campagnes.obtenirStatsCampagne,
    emailModalCampagneId ? { campagneId: emailModalCampagneId } : "skip",
  );

  const toggleParcours = (parcoursId: Id<"parcoursEvaluations">) => {
    setSelectedParcoursIds((prev) =>
      prev.includes(parcoursId)
        ? prev.filter((id) => id !== parcoursId)
        : [...prev, parcoursId],
    );
  };

  const toggleAllParcours = () => {
    if (!campagneStats) return;
    if (selectedParcoursIds.length === campagneStats.parcours.length) {
      setSelectedParcoursIds([]);
    } else {
      setSelectedParcoursIds(campagneStats.parcours.map((p) => p._id));
    }
  };

  const handleSendEmails = async () => {
    if (!emailModalCampagneId || selectedParcoursIds.length === 0) return;
    setSendingEmails(true);
    try {
      const res = await envoyerLiensParcoursSelectionnes({
        campagneId: emailModalCampagneId,
        parcoursIds: selectedParcoursIds,
      });
      alert(res.message);
      setEmailModalCampagneId(null);
      setSelectedParcoursIds([]);
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSendingEmails(false);
    }
  };

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { campagnes, total, totalPages } = result;

  return (
    <div className="min-h-screen flex flex-col items-start">
      <div className="w-full container mx-auto p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Campagnes</h1>
            <p className="text-muted-foreground mt-1">
              {total} campagne(s) au total
            </p>
          </div>
          <Link to="/campagnes/creer">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Créer une campagne
            </Button>
          </Link>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Select
                value={filtreStatut}
                onValueChange={(value: FiltreStatutCampagne) => {
                  setFiltreStatut(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tous les statuts</SelectItem>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="terminee">Terminée</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => {
                  setFiltreStatut("tous");
                  setPage(1);
                }}
              >
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">Nom</TableHead>
                  <TableHead className="w-[12%]">Type</TableHead>
                  <TableHead className="w-[12%]">Template</TableHead>
                  <TableHead className="w-[10%]">Candidats</TableHead>
                  <TableHead className="w-[15%]">Progression</TableHead>
                  <TableHead className="w-[10%]">Statut</TableHead>
                  <TableHead className="w-[11%]">Date création</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campagnes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <p className="text-muted-foreground">
                        Aucune campagne trouvée
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  campagnes.map((c) => (
                    <TableRow
                      key={c._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        (window.location.href = `/campagnes/${c._id}`)
                      }
                    >
                      <TableCell>
                        <div className="font-medium">{c.nom}</div>
                      </TableCell>
                      <TableCell className="max-w-0">
                        <Badge variant="outline" className="max-w-full truncate block">{c.typeParcours}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.evaluationsConfig.map((e) => e.template).filter(Boolean).join(", ")}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{c.nbCandidats}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="min-w-[100px]">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span>{c.progression.pourcentage}%</span>
                            <span className="text-muted-foreground">
                              {c.progression.terminees}/{c.progression.total}
                            </span>
                          </div>
                          <Progress
                            value={c.progression.pourcentage}
                            className="h-2"
                          />
                        </div>
                      </TableCell>
                      <TableCell>{getStatutBadge(c.statut)}</TableCell>
                      <TableCell>{formatDate(c.dateCreation)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setAddCandidatsCampagne({
                                id: c._id,
                                nom: c.nom,
                                anonyme: c.anonyme,
                              });
                            }}
                            title="Ajouter des candidats"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setSelectedParcoursIds([]);
                              setEmailModalCampagneId(c._id);
                            }}
                            title="Envoyer les liens par email"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="icon"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} sur {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {addCandidatsCampagne && (
        <AjouterCandidatsDialog
          isOpen={true}
          onClose={() => setAddCandidatsCampagne(null)}
          campagneId={addCandidatsCampagne.id}
          campagneNom={addCandidatsCampagne.nom}
          anonyme={addCandidatsCampagne.anonyme}
        />
      )}

      <Dialog
        isOpen={emailModalCampagneId !== null}
        onClose={() => {
          setEmailModalCampagneId(null);
          setSelectedParcoursIds([]);
        }}
        title="Envoyer les liens par email"
        className="max-w-2xl"
      >
        <div className="space-y-4">
          {campagneStats === undefined && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2">Chargement des candidats...</span>
            </div>
          )}

          {campagneStats && campagneStats.parcours.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {selectedParcoursIds.length} candidat(s) sélectionné(s) sur{" "}
                  {campagneStats.parcours.length}
                </p>
                <Button variant="ghost" size="sm" onClick={toggleAllParcours}>
                  {selectedParcoursIds.length === campagneStats.parcours.length
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </Button>
              </div>

              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Candidat</TableHead>
                      <TableHead>Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campagneStats.parcours.map((p) => (
                      <TableRow
                        key={p._id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleParcours(p._id)}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedParcoursIds.includes(p._id)}
                            onCheckedChange={() => toggleParcours(p._id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {p.prenomCandidat} {p.nomCandidat}
                        </TableCell>
                        <TableCell>{p.emailCandidat}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEmailModalCampagneId(null);
                    setSelectedParcoursIds([]);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  disabled={selectedParcoursIds.length === 0 || sendingEmails}
                  onClick={handleSendEmails}
                >
                  {sendingEmails ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Envoyer ({selectedParcoursIds.length})
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {campagneStats && campagneStats.parcours.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Aucun candidat dans cette campagne.
            </p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
